import { Plugin } from "flatpickr/dist/types/options";
import { CustomTimePickerConfig } from "../DatePickerBase.types";
import {
  createDropdownElement,
  filterDropdownOptions,
  generateTimeOptions,
  toggleDropdownVisibility
} from "./utils";

const timeDropdownPlugin = (config: CustomTimePickerConfig): Plugin => {
  const { minuteStep = 30 } = config;

  return (flatpickrInstance) => {
    let dropdown: HTMLDivElement;
    let preventClose = false;

    // Create dropdown structure
    const createDropdown = () => {
      dropdown = createDropdownElement("time-dropdown");
      dropdown.style.display = "none";
      document.body.appendChild(dropdown);
      populateDropdown('');
    };

    // Close dropdown when focus is lost
    const closeDropdown = () => {
      flatpickrInstance.calendarContainer.style.display = "none";
      toggleDropdownVisibility(dropdown, false);
    };

    const handleTimeOptionClick = (time: string) => {
      const selectedDate = flatpickrInstance.selectedDates.length > 0 ? flatpickrInstance.selectedDates[0] : new Date();
      const [hours, minutes] = time.split(":");
      const isPM = time.includes("PM");
      const adjustedHours = isPM
        ? (parseInt(hours, 10) % 12) + 12
        : parseInt(hours, 10) % 12;

      selectedDate.setHours(adjustedHours, parseInt(minutes, 10));
      flatpickrInstance.setDate(selectedDate, false); // Update selected date
      closeDropdown();
      flatpickrInstance.close();
    }

    // Populate dropdown with time options based on the query
    const populateDropdown = (query: string) => {
      if (!dropdown) return;
      dropdown.innerHTML = ""; // Clear previous items
      flatpickrInstance.calendarContainer.style.display = "flex";
      // Filter the options based on the query using filterDropdownOptions
      filterDropdownOptions(dropdown, query, generateTimeOptions(minuteStep), handleTimeOptionClick, closeDropdown);
    };

    // Input change handler
    const onInputChange = (e: Event) => {
      e.preventDefault(); // Prevent default Flatpickr parsing
      const input = e.target as HTMLInputElement;
      const query = input.value.trim();

      // Don't let Flatpickr format the input value
      flatpickrInstance._input.value = query;
      if (flatpickrInstance.altInput) {
        flatpickrInstance.altInput.value = query;
      }

      // Populate dropdown based on the query
      populateDropdown(query);
      toggleDropdownVisibility(dropdown, true);
    };

    const handleInputBlur = () => {
      if (preventClose) {
        preventClose = false;
        return;
      }
      closeDropdown();
      flatpickrInstance.close();
    }

    return {
      onReady() {
        const wrapper = flatpickrInstance.calendarContainer;

        // Create dropdown and append it to the calendar container
        createDropdown();
        if (dropdown) {
          wrapper.appendChild(dropdown);
        }
        const inputElement = flatpickrInstance.altInput;
        if (inputElement) {
          inputElement.addEventListener("input", onInputChange);
          inputElement.addEventListener("focus", () => populateDropdown(''));
          // Close dropdown when input loses focus
          dropdown?.addEventListener("mousedown", () => (preventClose = true));
          inputElement.addEventListener("blur", handleInputBlur);

          // Prevent autocomplete in the input field
          inputElement.setAttribute("autocomplete", "off");
        }
      },
    };
  };
};

export default timeDropdownPlugin;
